import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  BadgeText,
  Box,
  Heading,
  HStack,
  Pressable,
  ScrollView,
  Text,
  VStack,
} from '@gluestack-ui/themed';
import { SafeAreaView } from '@/common/components/SafeAreaView';
import { useTenantSettingsStore } from '@/common/context/TenantSettingsStore';
import GradeReport from '@/student/grade/components/GradeReport';
import ExamAttemptNote from '@/student/grade/components/ExamAttemptNote';
import { ModuleSubmissionState } from '@/common/api/models/moduleSubmissionState';
import { submissionActions } from '@/admin/common/objectActions/submissionActions';
import { useDataTableColumns } from '@/admin/common/DataTable/hooks/useDataTableColumns';
import type { ActionConfig } from '@/admin/common/DataTable/actionTypes';
import { playgroundTenantSettings } from '@/student/grade/fixtures/playgroundTenantSettings';
import {
  DEFAULT_APPROVAL_PRESET_ID,
  GRADE_REPORT_APPROVAL_PRESET_IDS,
  GRADE_REPORT_APPROVAL_PRESETS,
  gradedTabsForPreset,
  resolveApprovalPresetId,
  snapshotForPreset,
  type GradeReportApprovalPresetId,
} from '@/student/grade/fixtures/gradeReportApprovalPresets';

const MENU_ACTIONS: [string, ActionConfig][] = [
  'returnSubmissionToStudent',
  'returnSubmissionToLecturer',
  'grantAdditionalAttempt',
  'deleteSubmission',
].map((code) => {
  const action = submissionActions[code];
  return [code, { ...action, id: code, handler: () => undefined } as ActionConfig];
});

function menuRow(nextAction: 'take_next_attempt' | 'exhausted') {
  return {
    state: ModuleSubmissionState.graded,
    examAttemptsAllowed: 3,
    examNextAction: nextAction,
  };
}

type MenuColumn = {
  id?: string;
  cell?: (info: { row: { original: ReturnType<typeof menuRow> } }) => React.ReactNode;
};

function readInitialPresetId(): GradeReportApprovalPresetId {
  if (typeof window === 'undefined') {
    return DEFAULT_APPROVAL_PRESET_ID;
  }
  return resolveApprovalPresetId(window.location.search);
}

function replacePresetInUrl(presetId: GradeReportApprovalPresetId) {
  if (typeof window === 'undefined') {
    return;
  }
  const params = new URLSearchParams(window.location.search);
  params.set('preset', presetId);
  const query = params.toString();
  const next = query ? `${window.location.pathname}?${query}` : window.location.pathname;
  window.history.replaceState(null, '', next);
}

/**
 * Visual Lab chapter — production grade report with named approval presets.
 */
export function GradeReportLabScreen() {
  const { t } = useTranslation();
  const [presetId, setPresetId] = useState<GradeReportApprovalPresetId>(readInitialPresetId);
  const preset = GRADE_REPORT_APPROVAL_PRESETS[presetId];
  const gradedTabs = useMemo(() => gradedTabsForPreset(preset), [preset]);
  const latestIndex = gradedTabs.length > 0 ? gradedTabs.length - 1 : 0;
  const [selectedAttemptIndex, setSelectedAttemptIndex] = useState(
    preset.initialSelectedIndex ?? latestIndex,
  );

  useEffect(() => {
    const previous = useTenantSettingsStore.getState().settings;
    useTenantSettingsStore.setState({
      settings: playgroundTenantSettings,
      status: 'success',
    });

    return () => {
      if (previous) {
        useTenantSettingsStore.setState({
          settings: previous,
          status: 'success',
        });
      }
    };
  }, []);

  useEffect(() => {
    setPresetId(readInitialPresetId());
  }, []);

  useEffect(() => {
    const next = GRADE_REPORT_APPROVAL_PRESETS[presetId];
    const tabs = gradedTabsForPreset(next);
    const latest = tabs.length > 0 ? tabs.length - 1 : 0;
    setSelectedAttemptIndex(next.initialSelectedIndex ?? latest);
  }, [presetId]);

  const selectPreset = useCallback((nextId: GradeReportApprovalPresetId) => {
    setPresetId(nextId);
    replacePresetInUrl(nextId);
  }, []);

  const actionColumns = useDataTableColumns({
    tableColumns: {},
    includeDeprecated: false,
    handleSort: () => undefined,
    sortField: null,
    sortDirection: 'asc',
    singleLine: true,
    enableSelection: false,
    isSelectionMode: false,
    isActionRunning: false,
    rowActions: MENU_ACTIONS,
    objectName: 'reports/submissions',
  });
  const actionsColumn = (actionColumns as unknown as MenuColumn[]).find(
    (column) => column.id === 'actions',
  );

  const activeSnapshot = useMemo(
    () => snapshotForPreset(preset, selectedAttemptIndex),
    [preset, selectedAttemptIndex],
  );

  const previewModule = useMemo(() => ({
    ...preset.module,
    grading: {
      ...preset.module.grading,
      grade: activeSnapshot.grade,
      comment: activeSnapshot.comment,
    },
  }), [preset.module, activeSnapshot.grade, activeSnapshot.comment]);

  const reportTasks = activeSnapshot.tasks;
  const viewingEarlier = gradedTabs.length >= 2 && selectedAttemptIndex !== latestIndex;
  const archiveState = viewingEarlier && preset.archiveState && preset.archiveState !== 'ready'
    ? preset.archiveState
    : 'ready';

  return (
    <SafeAreaView flex={1} w="100%" pt={28}>
      <ScrollView flex={1} $base-px={20}>
        <Box px="$4" py="$6" maxWidth={900} alignSelf="center" width="100%">
          <VStack space="lg">
            <VStack space="xs">
              <Heading size="lg">{t('grade.playground.title')}</Heading>
              <Text size="sm" color="$textSubtle">
                {t('grade.playground.hint')}
              </Text>
            </VStack>

            <Box width="100%" overflow="hidden">
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <HStack
                  space="md"
                  alignItems="center"
                  flexWrap="nowrap"
                  testID="playground-preset-chips"
                >
                  {GRADE_REPORT_APPROVAL_PRESET_IDS.map((id) => {
                    const selected = id === presetId;
                    const chip = GRADE_REPORT_APPROVAL_PRESETS[id];
                    return (
                      <Pressable
                        key={id}
                        onPress={() => selectPreset(id)}
                        testID={`playground-preset-${id}`}
                      >
                        <Badge
                          variant={selected ? 'solid' : 'outline'}
                          action={selected ? 'info' : 'muted'}
                        >
                          <BadgeText>{chip.chipLabel}</BadgeText>
                        </Badge>
                      </Pressable>
                    );
                  })}
                </HStack>
              </ScrollView>
            </Box>

            <Box
              borderTopWidth={1}
              borderTopColor="$borderLight200"
              pt="$6"
              testID="grade-report-playground-preview"
            >
              <GradeReport
                module={previewModule}
                tasks={reportTasks}
                gradedAttempts={gradedTabs}
                selectedAttemptIndex={selectedAttemptIndex}
                onSelectAttempt={setSelectedAttemptIndex}
                archiveState={archiveState}
                onRetryArchive={() => undefined}
                afterHeader={(
                  <ExamAttemptNote
                    status={preset.module.examAttempt}
                    busy={preset.noteBusy}
                    error={preset.noteError}
                  />
                )}
              />
            </Box>

            <HStack space="xl" testID="grant-menu-preview" flexWrap="wrap">
              {([
                ['attempts-remain', 'Attempts remain', 'take_next_attempt'],
                ['none-remain', 'No attempts left', 'exhausted'],
              ] as const).map(([id, label, nextAction]) => (
                <VStack key={id} space="sm" minWidth={280}>
                  <Text size="sm" color="$textSubtle">{label}</Text>
                  <HStack
                    bg="$bgCard"
                    borderWidth={1}
                    borderColor="$borderLight"
                    borderRadius="$md"
                    px="$4"
                    py="$3"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Text size="sm">Harry Potter</Text>
                    {actionsColumn?.cell?.({ row: { original: menuRow(nextAction) } })}
                  </HStack>
                </VStack>
              ))}
            </HStack>
          </VStack>
        </Box>
      </ScrollView>
    </SafeAreaView>
  );
}
